import { useEffect, useRef, useState } from "react";
import { useInView } from "framer-motion";
import { Reveal, SectionTag } from "./shared";
import { Zap, Network, BadgeCheck, ShieldCheck } from "lucide-react";

const CountUp = ({ value, prefix = "$", suffix = "M" }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const [n, setN] = useState(value);
  useEffect(() => {
    if (!inView) return;
    setN(0);
    const start = performance.now();
    const dur = 1400;
    const tick = (t) => {
      const p = Math.min((t - start) / dur, 1);
      setN(Math.round(value * (1 - Math.pow(1 - p, 3))));
      if (p < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, [inView, value]);
  return <span ref={ref}>{prefix}{n}{suffix}</span>;
};

const stats = [
  { label: "Trapped Cash Identified", value: 30 },
  { label: "Recovered in 90 Days", value: 24 },
  { label: "Deleveraging Capacity (4x leverage)", value: 96 },
  { label: "Or: Distribution to Fund", value: 24 },
];

const reasons = [
  { icon: Zap, title: "Speed", text: "Seven days. Your ops team cannot deliver this in 16 weeks — the manual diagnostics are sequential, high-friction, custom per company. This runs parallel across your entire portfolio and completes in one diagnostic cycle." },
  { icon: Network, title: "Portfolio Leverage", text: "Same framework, same five pillars, same fee across every manufacturing name. You're not running 10 separate projects; you're scaling one standardized process. All diagnostics run in parallel; portfolio results complete in 4–6 weeks." },
  { icon: BadgeCheck, title: "Validated by Their CFO", text: "Every number is signed off before you see it. Your management team owns the findings and the 90-day plan. That turns a consultant report into an execution commitment." },
  { icon: ShieldCheck, title: "No Disruption", text: "Data-only diagnostic. Five department leader interviews total, not 30+. Your ops teams don't get pulled off the floor; they get handed a roadmap and own the execution." },
];

export const PEMath = () => (
  <section id="pe-math" className="py-12 lg:py-16 bg-[#f7f9fa] dot-grid" data-testid="pe-math-section">
    <div className="max-w-[1320px] mx-auto px-6 lg:px-10">
      <SectionTag num="05" label="The PE Math" />
      <div className="grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <Reveal>
            <h2 className="font-display font-extrabold text-[#0B1B2B] tracking-[-0.02em] leading-[1.05] text-3xl sm:text-4xl lg:text-[44px]">
              Trapped cash converts to <span className="text-[#0E5F46]">real returns.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-6 text-[#3c4a58] leading-relaxed text-base md:text-lg">
              One company finding $5M is good. Your portfolio finding $50M is a distribution check.
            </p>
            <p className="mt-4 text-[13px] font-bold uppercase tracking-[0.16em] text-[#0B1B2B]/60">
              Example: $511M revenue portfolio company
            </p>
          </Reveal>
        </div>
        <div className="lg:col-span-7">
          <div className="grid sm:grid-cols-2 gap-4">
            {stats.map((s, i) => (
              <Reveal key={s.label} delay={0.08 * i}>
                <div className="p-7 rounded-2xl bg-white border border-[#0B1B2B]/8 hover:shadow-[0_16px_44px_rgba(11,27,43,0.09)] transition-shadow duration-500" data-testid={`math-stat-${i + 1}`}>
                  <p className="font-display font-extrabold text-[#0E5F46] text-4xl lg:text-[44px] tracking-[-0.02em]">
                    <CountUp value={s.value} />
                  </p>
                  <p className="mt-2 text-[#3c4a58] text-sm font-medium">{s.label}</p>
                </div>
              </Reveal>
            ))}
            <Reveal delay={0.35} className="sm:col-span-2">
              <div className="p-7 rounded-2xl bg-[#0E5F46] text-white flex flex-wrap items-baseline gap-x-4 gap-y-1" data-testid="math-stat-ebitda">
                <p className="font-display font-extrabold text-3xl lg:text-4xl tracking-[-0.02em]">EBITDA Uplift</p>
                <p className="text-white/85 font-semibold">80 bps to working capital efficiency</p>
              </div>
            </Reveal>
          </div>
        </div>
      </div>

      <Reveal delay={0.1}>
        <h3 className="mt-10 font-display font-extrabold text-[#0B1B2B] tracking-[-0.02em] text-2xl sm:text-3xl">There are four key benefits to this process.</h3>
      </Reveal>
      <div className="mt-6 grid md:grid-cols-2 gap-5">
        {reasons.map((r, i) => (
          <Reveal key={r.title} delay={0.07 * i}>
            <div className="h-full p-8 rounded-2xl bg-white border border-[#0B1B2B]/8 hover:-translate-y-1 hover:shadow-[0_20px_50px_rgba(11,27,43,0.09)] transition-all duration-500" data-testid={`why-card-${i + 1}`}>
              <div className="flex items-center gap-3.5">
                <div className="w-10 h-10 rounded-lg bg-[#0E5F46]/10 flex items-center justify-center shrink-0">
                  <r.icon className="w-5 h-5 text-[#0E5F46]" strokeWidth={2} />
                </div>
                <h4 className="font-display font-bold text-[#0B1B2B] text-lg">{r.title}</h4>
              </div>
              <p className="mt-4 text-[#3c4a58] text-sm leading-relaxed">{r.text}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);
